import { Component, EventEmitter, inject, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { MatChipsModule } from '@angular/material/chips'
import { MatIcon } from '@angular/material/icon'
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay'
import { AsyncPipe, DatePipe } from '@angular/common'
import { ActivatedRoute, Router } from '@angular/router'
import { filter, map, Observable } from 'rxjs'
import { Attendee, GuestOrganization } from '../guest-organizations.service'
import { MatFormField, MatSuffix } from '@angular/material/input'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core'
import { MatButton } from '@angular/material/button'
import { FilterComponent } from './filter/filter.component'
import { anyFiltersApplied, MeetingsService, Speaker } from '../meetings.service'
import { MatDatepickerModule, MatDatepickerToggle } from '@angular/material/datepicker'
import { MatHint, MatLabel } from '@angular/material/form-field'
import { endOfDay, startOfDay } from 'date-fns'
import { MatCheckbox } from '@angular/material/checkbox'

export type MeetingFilters = {
  query: string | null
  guestOrganizationId: number | null
  attendeeId: string | null
  speakerIds: string[]
  startDate: Date | null
  endDate: Date | null
  showOnlyPastMeetings: boolean
}

@Component({
  selector: 'app-filters',
  standalone: true,
  imports: [
    MatChipsModule,
    MatIcon,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    AsyncPipe,
    ReactiveFormsModule,
    TranslatePipe,
    TranslateDirective,
    MatButton,
    FilterComponent,
    MatDatepickerModule,
    MatSuffix,
    DatePipe,
    MatFormField,
    MatLabel,
    FormsModule,
    MatHint,
    MatCheckbox,
  ],
  templateUrl: './filters.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './filters.component.scss',
})
export class FiltersComponent {
  @Input()
  filters!: MeetingFilters

  @Output()
  resetFilters = new EventEmitter<void>()

  @ViewChild(MatDatepickerToggle)
  dateRangePicker!: MatDatepickerToggle<any>

  router = inject(Router)
  route = inject(ActivatedRoute)
  meetingsService = inject(MeetingsService)
  anyFiltersApplied = anyFiltersApplied

  guestOrganizations$ = this.meetingsService.getSearchGuestOrganizationOptions().pipe(
    filter((result) => result.isSuccess),
    map((result) => result.data ?? [])
  ) as Observable<GuestOrganization[]>
  attendees$ = this.meetingsService.getSearchAttendeeOptions().pipe(
    filter((result) => result.isSuccess),
    map((result) => result.data ?? [])
  ) as Observable<Attendee[]>
  // speakers$ = this.callsService.getSearchSpeakerOptions().pipe(
  //   filter((result) => result.isSuccess),
  //   map((result) => result.data ?? [])
  // ) as Observable<Speaker[]>

  isDateRangeOverlayOpen = false
  startDate: Date | null = null
  endDate: Date | null = null

  setGuestOrganizationId(guestOrganizationId: number | null) {
    this.setFilter({ filterGuestOrganizationId: guestOrganizationId })
  }

  findGuestOrganization(guestOrganizations: GuestOrganization[], guestOrganizationId: number): GuestOrganization | null {
    return guestOrganizations.find((guestOrganization) => guestOrganization.id === guestOrganizationId) ?? null
  }

  filterGuestOrganizations(query: string, guestOrganizations: GuestOrganization[]): GuestOrganization[] {
    return guestOrganizations.filter((guestOrganization) => guestOrganization.name.toLowerCase().includes(query.toLowerCase()))
  }

  getTeamName(guestOrganization: GuestOrganization | null) {
    return guestOrganization !== null ? guestOrganization.name : ''
  }

  setAttendeeId(attendeeId: string | null) {
    this.setFilter({ filterAttendeeId: attendeeId })
  }

  findAttendee(attendees: Attendee[], attendeeId: string): Attendee | null {
    return attendees.find((attendee) => attendee.email === attendeeId) ?? null
  }

  filterAttendees(query: string, attendees: Attendee[]): Attendee[] {
    return attendees.filter((attendee) => attendee.name.toLowerCase().includes(query.toLowerCase()))
  }

  getAttendeeName(attendee: Attendee | null) {
    return attendee !== null ? attendee.name : ''
  }

  setSpeakerIds(speakerIds: string[]) {
    this.setFilter({ filterSpeakerIds: speakerIds })
  }

  getSpeakerName(speaker: Speaker) {
    return speaker.name ?? speaker.speakerTag
  }

  private setFilter(filter: any) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: filter,
      queryParamsHandling: 'merge',
    })
  }

  reset() {
    this.resetFilters.emit()
  }

  isDateRangeSelected(filters: MeetingFilters): boolean {
    return filters.startDate !== null || filters.endDate !== null
  }

  setDateRange() {
    this.setFilter({ filterStartDate: this.startDate, filterEndDate: this.endDate })
    this.isDateRangeOverlayOpen = false
  }

  resetDateRange() {
    this.setFilter({ filterStartDate: null, filterEndDate: null })
    this.isDateRangeOverlayOpen = true
    this.openDateRangeCalendar()
  }

  displaySelectedSpeakers(selectedSpeakers: string[]): string {
    return selectedSpeakers.join(', ')
  }

  private openDateRangeCalendar() {
    // Using the opened input of the calendar makes the calendar appear on top left corner since there is no event for x and y coordinates
    setTimeout(() => this.dateRangePicker._button._elementRef.nativeElement.click(), 0)
  }

  setStartDate(date: Date) {
    this.startDate = startOfDay(date)
  }

  setEndDate(date: Date) {
    this.endDate = endOfDay(date)
  }

  setShowOnlyPastMeetings(value: boolean) {
    this.setFilter({ filterShowOnlyPastMeetings: value })
  }
}
